import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const SanityTest = () => {
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const runSanityTest = async () => {
    setLoading(true);
    try {
      // Simulerad CSV data från modules/skattekonto_dummy.csv
      const csvData = `Bokföringsdag,Text,Period,Belopp,Saldo,Referens
2024-01-12,Debiterad preliminärskatt,, -15000, -15000,FSK-2024-01
2024-01-15,Inbetalning,,15000, 0,Betalning BG123
2024-02-12,Debiterad arbetsgivaravgift,2024-01,-45000, -45000,AGI-2024-01
2024-02-20,Inbetalning,,45000, 0,Betalning BG123
2024-03-12,Debiterad arbetsgivaravgift,2024-02,-47000, -47000,AGI-2024-02
2024-03-15,Kostnadsränta,, -37, -47037,Ränta
2024-03-18,Inbetalning,,47037, 0,Betalning BG123
2024-04-12,Debiterad moms,2024-Q1,-60000, -60000,MOMS-Q1
2024-04-22,Inbetalning,,60000, 0,Betalning BG123`;

      // Parse CSV
      const lines = csvData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      const recs = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v?.trim());
        const record: any = {};
        headers.forEach((header, i) => {
          record[header] = values[i];
        });
        return record;
      });

      console.log("[SANITY] parsed rows:", recs.length);
      console.log("[SANITY] first 3 raw records:", recs.slice(0, 3));

      // A) Header mappning med synonymer
      const synonyms: Record<string, string[]> = {
        Förfallodatum: ["förfallodatum","forfallodatum","förf.datum","forf.datum","due","due_date","duedate"],
        DeklareratBelopp: ["deklareratbelopp","deklarerat_belopp","declared","declared_amount","deklaration","deklarationsbelopp"],
        Händelse: ["händelse","handelse","event","typ","transaction_type","beskrivning","text"],
        Belopp: ["belopp","amount","summa","belopp (sek)","belopp_sek","amt"],
        Datum: ["datum","date","bokföringsdatum","transaktionsdatum","postdatum","bokföringsdag"],
        TransaktionsID: ["transaktionsid","id","tx","tx_id","tx-id","trans.id","refid","referenceid","referens"]
      };

      const mapHeaders = (headers: string[]): Record<string, string> => {
        const mapping: Record<string, string> = {};
        const normalizedHeaders = headers.map(h => h.normalize("NFKC").replace(/\uFEFF/g,"").trim().toLowerCase());
        
        for (const [canonical, variants] of Object.entries(synonyms)) {
          for (const variant of variants) {
            const idx = normalizedHeaders.indexOf(variant);
            if (idx >= 0) {
              mapping[canonical] = headers[idx];
              break;
            }
          }
        }
        return mapping;
      };

      const mapping = mapHeaders(headers);
      console.log("[AUDIT DEBUG] header mapping:", mapping);
      
      if (!mapping.Datum || !mapping.Belopp || !mapping.Händelse) {
        throw new Error("MISSING_REQUIRED_HEADERS(Datum/Belopp/Händelse)");
      }

      // B) Förbättrade helpers
      const norm = (s?: string|null) => (s ?? "").normalize("NFKC").replace(/\uFEFF/g,"").trim().toLowerCase();
      
      const toNumber = (x: any): number | null => {
        if (x == null) return null;
        if (typeof x === "number") return Number.isFinite(x) ? x : null;
        const s0 = String(x).normalize("NFKC").replace(/\uFEFF/g,"").trim();
        const s1 = s0.replace(/\s+/g,"").replace(/,(?=\d{3}\b)/g,""); // tusental-komma
        const s2 = s1.includes(",") && !s1.includes(".") ? s1.replace(",", ".") : s1; // svensk decimal
        const n = Number(s2);
        return Number.isFinite(n) ? n : null;
      };
      
      const toIsoDate = (d?: string|null): string|null => {
        if (!d) return null;
        const s = d.normalize("NFKC").replace(/\uFEFF/g,"").trim();
        const iso = s.length >= 10 ? s.slice(0,10) : s; // ta bara YYYY-MM-DD
        const t = Date.parse(iso);
        return Number.isFinite(t) ? iso : null;
      };

      // C) Regel-detektering
      const isDeclaration = (h: string): boolean => {
        const s = norm(h);
        return s.includes("skattedeklaration moms")
            || s.includes("skattedeklaration arbetsgivaravgifter")
            || s.includes("debiterad arbetsgivaravgift")   // ← din logg
            || s.includes("debiterad preliminärskatt")
            || s.includes("debiterad preliminarskatt");
      };

      const isFeeOrInterest = (h: string): boolean => {
        const s = norm(h);
        if (isDeclaration(s)) return false; // ← viktigt: exkludera deklarationer
        return s.includes("avgift") || s.includes("ränta") || s.includes("ranta") || s.includes("kostnadsränta");
      };

      // D) Fallback för förfallodatum
      const inferDueDate = (row: any): string | null => {
        if (!row.Datum || !row.Händelse) return null;
        const d = new Date(row.Datum);
        const s = norm(row.Händelse);
        if (s.includes("skattedeklaration moms") || s.includes("skattedeklaration arbetsgivaravgifter")) {
          const y = d.getMonth() === 11 ? d.getFullYear()+1 : d.getFullYear();
          const m = d.getMonth() === 11 ? 0 : d.getMonth()+1;
          const day = 17; // förenklad default
          return new Date(Date.UTC(y, m, day)).toISOString().slice(0,10);
        }
        if (s.includes("debiterad preliminärskatt") || s.includes("debiterad preliminarskatt")) {
          // ofta samma månad den 12:e/17:e – välj 17:e för enkelhet
          return new Date(Date.UTC(d.getFullYear(), d.getMonth(), 17)).toISOString().slice(0,10);
        }
        return null;
      };

      // Normalize rows
      const rows = recs.map((r, i) => {
        const baseRow = {
          TransaktionsID: r[mapping.TransaktionsID] || `TX-${String(i+1).padStart(6,"0")}`,
          Datum: toIsoDate(r[mapping.Datum]),
          Händelse: (r[mapping.Händelse] ?? "").normalize("NFKC").trim(),
          Belopp: toNumber(r[mapping.Belopp]) ?? 0,
          DeklareratBelopp: toNumber(r[mapping.DeklareratBelopp]) ?? 0,
          Saldo: toNumber(r[mapping.Saldo]) ?? null,
        };
        
        return {
          ...baseRow,
          Förfallodatum: toIsoDate(r[mapping.Förfallodatum]) ?? inferDueDate(baseRow),
        };
      });

      console.log("[AUDIT DEBUG] counts:", {
        rows: rows.length,
        withDate: rows.filter(r=>!!r.Datum).length,
        withAmount: rows.filter(r=>Number.isFinite(r.Belopp)).length,
        withDeclared: rows.filter(r=>Number.isFinite(r.DeklareratBelopp as number) && (r.DeklareratBelopp as number)!==0).length,
        declarations: rows.filter(r=>r.Händelse && isDeclaration(r.Händelse)).length,
        fees: rows.filter(r=>r.Händelse && isFeeOrInterest(r.Händelse)).length
      });

      console.log("[SANITY] normalized rows:", rows.slice(0, 3));

      // Pre-counters
      const inbetalningar = rows.filter(r => norm(r.Händelse) === "inbetalning");
      const inbetalnMedFörf = inbetalningar.filter(r => !!r.Förfallodatum);
      const inbetalnSena = inbetalnMedFörf.filter(r => {
        const d = new Date(r.Datum!).getTime();
        const f = new Date(r.Förfallodatum!).getTime();
        return Number.isFinite(d) && Number.isFinite(f) && Math.floor((d - f)/86400000) > 3;
      });

      const feeRows = rows.filter(r => r.Händelse && isFeeOrInterest(r.Händelse));
      const declRows = rows.filter(r => r.Händelse && isDeclaration(r.Händelse));
      const declPairs = rows.filter(r => (r.DeklareratBelopp ?? 0) !== 0 && Number.isFinite(r.Belopp));
      const declHits = declPairs.filter(r => {
        const diff = Math.abs(Math.abs(r.Belopp) - Math.abs(r.DeklareratBelopp!));
        return Math.abs(r.DeklareratBelopp!) > 0 && (diff / Math.abs(r.DeklareratBelopp!)) > 0.15;
      });

      // Prelim per månad
      const ym = (d: string) => `${new Date(d).getFullYear()}-${String(new Date(d).getMonth()+1).padStart(2,"0")}`;
      const prelim = rows.filter(r => {
        const s = norm(r.Händelse);
        return s.includes("preliminärskatt") || s.includes("preliminarskatt");
      });

      // Debug förklaringar
      const debugInfo = {
        headerMapping: mapping,
        inbetalningarWithDue: `Förfallodatum finns på ${inbetalnMedFörf.length}/${inbetalningar.length} inbetalningar`,
        declWithAmount: `DeklareratBelopp != 0 på ${declPairs.length}/${declRows.length} deklarationsrader`,
        feeExplanation: `Avgifter exkluderar nu deklarationer (Debiterad arbetsgivaravgift räknas som deklaration)`
      };

      const testResults = {
        totalRows: recs.length,
        normalizedRows: rows.length,
        inbetalningar: inbetalningar.length,
        inbetalnMedFörf: inbetalnMedFörf.length,
        inbetalnSena: inbetalnSena.length,
        feeRows: feeRows.length,
        declRows: declRows.length,
        declPairs: declPairs.length,
        declHits: declHits.length,
        prelim: prelim.length,
        debugInfo,
        sampleNormalizedRows: rows.slice(0, 3),
        sampleInbetalningar: inbetalningar,
        sampleFeeRows: feeRows,
        sampleDeclRows: declRows,
        samplePrelim: prelim
      };

      console.log("[SANITY] precounts:", testResults);
      setResults(testResults);
    } catch (error) {
      console.error("[SANITY] Error:", error);
      setResults({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Sanity Test - Skattekonto Regel Logik</CardTitle>
        <Button onClick={runSanityTest} disabled={loading}>
          {loading ? 'Kör test...' : 'Kör Sanity Test'}
        </Button>
      </CardHeader>
      <CardContent>
        {results && (
          <div className="space-y-4">
            {results.error ? (
              <div className="text-red-600">
                <strong>Error:</strong> {results.error}
              </div>
            ) : (
              <>
                <div className="mb-4 p-3 bg-blue-50 rounded-md">
                  <h3 className="font-semibold text-blue-800 mb-2">Debug Information:</h3>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div><strong>Header mapping:</strong> {JSON.stringify(results.debugInfo.headerMapping)}</div>
                    <div><strong>Inbetalningar:</strong> {results.debugInfo.inbetalningarWithDue}</div>
                    <div><strong>Deklarationer:</strong> {results.debugInfo.declWithAmount}</div>
                    <div><strong>Avgifter:</strong> {results.debugInfo.feeExplanation}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold">Grundläggande räkning:</h3>
                    <ul className="space-y-1 text-sm">
                      <li>Totala rader: {results.totalRows}</li>
                      <li>Normaliserade rader: {results.normalizedRows}</li>
                      <li>Inbetalningar: {results.inbetalningar}</li>
                      <li>Inbetalningar med förfallodatum: {results.inbetalnMedFörf} 
                        {results.inbetalnMedFörf === 0 && <span className="text-orange-600 ml-2">(Förfallodatum saknas/estimerat)</span>}
                      </li>
                      <li>Sena inbetalningar (&gt;3 dagar): {results.inbetalnSena}
                        {results.inbetalnSena === 0 && results.inbetalnMedFörf === 0 && <span className="text-orange-600 ml-2">(Kräver förfallodatum)</span>}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold">Regelräkning:</h3>
                    <ul className="space-y-1 text-sm">
                      <li>Avgifter/Räntor: {results.feeRows}
                        {results.feeRows === 1 && <span className="text-green-600 ml-2">(Kostnadsränta räknas som avgift)</span>}
                      </li>
                      <li>Deklarationsrader: {results.declRows}
                        {results.declRows >= 3 && <span className="text-green-600 ml-2">(Inkl. Debiterad arbetsgivaravgift)</span>}
                      </li>
                      <li>Deklaration vs bokfört par: {results.declPairs}
                        {results.declPairs === 0 && <span className="text-orange-600 ml-2">(DeklareratBelopp=0 på alla)</span>}
                      </li>
                      <li>Avvikelser (&gt;15%): {results.declHits}</li>
                      <li>Preliminärskatt: {results.prelim}</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="font-semibold">Sample Normaliserade Rader:</h3>
                  <pre className="bg-gray-100 p-2 text-xs overflow-x-auto">
                    {JSON.stringify(results.sampleNormalizedRows, null, 2)}
                  </pre>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold">Sample Inbetalningar:</h3>
                    <pre className="bg-gray-100 p-2 text-xs overflow-x-auto">
                      {JSON.stringify(results.sampleInbetalningar, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <h3 className="font-semibold">Sample Avgifter:</h3>
                    <pre className="bg-gray-100 p-2 text-xs overflow-x-auto">
                      {JSON.stringify(results.sampleFeeRows, null, 2)}
                    </pre>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold">Sample Deklarationer:</h3>
                    <pre className="bg-gray-100 p-2 text-xs overflow-x-auto">
                      {JSON.stringify(results.sampleDeclRows, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <h3 className="font-semibold">Sample Preliminärskatt:</h3>
                    <pre className="bg-gray-100 p-2 text-xs overflow-x-auto">
                      {JSON.stringify(results.samplePrelim, null, 2)}
                    </pre>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SanityTest;